/** Compact task row for horizon and day-grid cells with optional drag and complete actions. */
import { useDraggable } from '@dnd-kit/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { getTaskProjects } from '@/features/tasks/utils/data/taskTags';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import TaskContextMenuTarget from '@/features/tasks/components/context-menu/TaskContextMenuTarget';
import { createTaskItemDoubleClickHandler } from '@/features/tasks/components/items/useTaskItemBaseController.helpers';
import { isMainTask } from '@/features/tasks/components/atoms/taskMetadataViewState';
import GridTaskItemContent from '@/features/planning/components/shared/GridTaskItemContent';

function resolveGridTaskItemState(task, title, project, projectDisplayMap) {
  const resolvedTitle = title ?? task?.title ?? '';
  const primaryProject = project ?? (task ? getTaskProjects(task)[0] : null);
  const projectLabel = primaryProject
    ? (projectDisplayMap.get(primaryProject.toLowerCase()) ?? primaryProject)
    : null;
  const canDrag = Boolean(task?.id);
  return { resolvedTitle, primaryProject, projectLabel, canDrag };
}

export default function GridTaskItem({
  task,
  title,
  project,
  showComplete = false,
  onToggleComplete,
  dense = false,
  roomy = false,
  keyboardActive = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectDisplayMap, tasksMap } = useTasksContext();
  const { resolvedTitle, primaryProject, projectLabel, canDrag } =
    resolveGridTaskItemState(task, title, project, projectDisplayMap);
  const mainTask = isMainTask(task, tasksMap);
  const handleDoubleClick =
    task?.id !== undefined && task?.id !== null
      ? createTaskItemDoubleClickHandler(navigate, location, task)
      : undefined;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: canDrag ? `grid-${task.id}` : 'grid-item',
    disabled: !canDrag,
    data: canDrag ? { task, type: 'unscheduled-task' } : undefined,
  });

  const content = (
    <GridTaskItemContent
      resolvedTitle={resolvedTitle}
      primaryProject={primaryProject}
      projectLabel={projectLabel}
      task={task}
      isMainTask={mainTask}
      showComplete={showComplete}
      onToggleComplete={onToggleComplete}
      dense={dense}
      roomy={roomy}
      canDrag={canDrag}
      setNodeRef={setNodeRef}
      listeners={listeners}
      attributes={attributes}
      isDragging={isDragging}
      onDoubleClick={handleDoubleClick}
      keyboardActive={keyboardActive}
      keyboardItemId={
        task?.id ? `calendar-scheduled-task-${task.id}` : undefined
      }
    />
  );

  if (!task?.id) return content;

  return (
    <TaskContextMenuTarget task={task} surface="plan-week-month">
      {content}
    </TaskContextMenuTarget>
  );
}
